import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/compat/router';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useQuery } from '@apollo/client';

import { GetPublicContextSessionDocument } from 'src/gql/graphql';
import useUI from 'src/hooks/use-ui';
import Button from 'src/components/core/button';
import { KIOSK_PRO_MEDIA } from 'src/components/kiosk-pro/kiosk-pro-shared/kiosk-pro-shared.styles';
import { ModalContainer } from 'shared/modals';
import { KioskLoginContinue } from 'src/components/kiosk-pro/kiosk-login/kiosk-login-continue';
import { useGetSessionId } from 'src/components/kiosk-pro/kiosk-pro-shared/use-session-id';
import { usePublicSessionLoginHandler } from 'src/components/kiosk-pro/kiosk-pro-shared/use-public-session-login-handler';
import { usePublicContextSessionPusher } from 'src/hooks/use-public-context-session-pusher';
import { DUPLICATE_PHONE_SIGN_IN_MESSAGE } from 'src/components/kiosk-pro/kiosk-pro-shared/duplicate-phone-message';
import { QrCodeLogin } from './components/qr-code-login';
import { PhoneNumberPad } from './components/phone-number-pad/phone-number-pad';

type KioskLoginModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  consumerPhoneLoginUrl: string;
};

export function KioskLoginModal({ isOpen, handleClose, consumerPhoneLoginUrl }: KioskLoginModalProps): JSX.Element {
  const UI = useUI();
  const router = useRouter();
  const publicLoginSessionId = useGetSessionId();
  const flags = useFlags();
  const isKioskProPhoneNumberLoginEnabled =
    flags['growth.ecomm.kiosk-pro-consumer-phone-number-log-in.rollout'] ?? false;

  const { data, refetch } = useQuery(GetPublicContextSessionDocument, {
    variables: {
      id: publicLoginSessionId,
    },
    pollInterval: 15000,
  });

  const [isLoginMethodVerified, setIsLoginMethodVerified] = useState(false);
  const [showPhoneNumberPad, setShowPhoneNumberPad] = useState(false);
  const [isAfterDuplicatePhone, setIsAfterDuplicatePhone] = useState(false);

  const { isUserLoggedInSuccessfully, userFirstName } = usePublicSessionLoginHandler(publicLoginSessionId);

  const updateLoginMethodVerified = useCallback((newValue: boolean | null) => {
    const value = newValue ?? false;
    setIsLoginMethodVerified(value);
  }, []);

  usePublicContextSessionPusher({
    sessionId: publicLoginSessionId,
    enabled: isOpen && !!publicLoginSessionId,
    onSessionUpdated: (sessionData) => {
      updateLoginMethodVerified(sessionData.loginMethodVerified);
      void refetch();
    },
    onLoginMethodVerified: (loginData) => {
      updateLoginMethodVerified(loginData.loginMethodVerified);
    },
  });

  const handleCloseModal = useCallback((): void => {
    UI.setIsKioskProLoginModalOpen(false);
    handleClose();
    setShowPhoneNumberPad(false);

    const queryWithoutSessionId = { ...router?.query };
    delete queryWithoutSessionId['session-id'];

    void router?.replace(
      {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- router can be null
        pathname: router?.pathname,
        query: queryWithoutSessionId,
      },
      undefined,
      { shallow: true, scroll: false }
    );
  }, [UI, handleClose, router]);

  const handleShowPhoneNumberPad = useCallback((): void => {
    setIsAfterDuplicatePhone(false);
    setShowPhoneNumberPad(true);
  }, []);

  const handleDuplicatePhone = useCallback((): void => {
    setShowPhoneNumberPad(false);
    setIsAfterDuplicatePhone(true);
  }, []);

  useEffect(() => {
    if (data?.getPublicContextSession?.loginMethodVerified !== undefined && !isLoginMethodVerified) {
      setIsLoginMethodVerified(data.getPublicContextSession.loginMethodVerified ?? false);
    }
  }, [data, isLoginMethodVerified]);

  useEffect(() => {
    // Skip auto-close when using phone number login - PhoneNumberLoginSuccess handles its own navigation
    if (!isUserLoggedInSuccessfully || showPhoneNumberPad) {
      return undefined;
    }

    const timeout = setTimeout(() => {
      handleCloseModal();
    }, 2000);

    return () => {
      clearTimeout(timeout);
    };
  }, [isUserLoggedInSuccessfully, showPhoneNumberPad, handleCloseModal]);

  const renderModalContent = (): JSX.Element => {
    if (isLoginMethodVerified) {
      return (
        <KioskLoginContinue
          isUserLoggedInSuccessfully={isUserLoggedInSuccessfully}
          userFirstName={userFirstName}
          isInModal
        />
      );
    }

    if (showPhoneNumberPad) {
      return <PhoneNumberPad isInModal onClose={handleCloseModal} onDuplicatePhone={handleDuplicatePhone} />;
    }

    return (
      <QRCodeLoginContainer>
        {isAfterDuplicatePhone && (
          <DuplicatePhoneMessage role='status' data-testid='kiosk-duplicate-phone-message'>
            {DUPLICATE_PHONE_SIGN_IN_MESSAGE}
          </DuplicatePhoneMessage>
        )}

        <ModalQrCodeLogin consumerPhoneLoginUrl={consumerPhoneLoginUrl} size={400} />

        {isKioskProPhoneNumberLoginEnabled && (
          <>
            <>Or</>
            <ModalPhoneNumberButton onClick={handleShowPhoneNumberPad}>Enter your phone #</ModalPhoneNumberButton>
          </>
        )}
      </QRCodeLoginContainer>
    );
  };

  return (
    <StyledModalContainer
      isOpen={isOpen}
      onRequestClose={handleCloseModal}
      width='820px'
      alignItems='center'
      padding='36px'
      borderRadius='20px'
      ariaHideApp={false}
      data-testid='kiosk-pro-login-modal'
    >
      {renderModalContent()}
    </StyledModalContainer>
  );
}

function KioskScrollableModalContainer(props: React.ComponentProps<typeof ModalContainer>): JSX.Element {
  return <ModalContainer {...props} onAfterOpen={() => undefined} />;
}

const StyledModalContainer = styled(KioskScrollableModalContainer)`
  max-width: 820px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  justify-content: center;
  border-radius: 20px;
  padding: 56px;

  @media (orientation: portrait) {
    max-height: 960px;
  }
  @media (orientation: landscape) {
    max-height: 964px;
  }

  @media ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    max-width: 560px;
    max-height: 780px;
    padding: 40px;
  }

  @media ${KIOSK_PRO_MEDIA.landscapeMaxWidth} {
    max-width: 720px;
    max-height: 745px;
    padding: 40px;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportPortrait}, ${KIOSK_PRO_MEDIA.smallViewportLandscapeOnly} {
    max-width: 680px;
    max-height: 574px;
    padding: 36px;
  }
`;

const QRCodeLoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  font-size: 24px;

  @media ${KIOSK_PRO_MEDIA.landscapeMaxWidth}, ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    font-size: 20px;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportPortrait}, ${KIOSK_PRO_MEDIA.smallViewportLandscapeOnly} {
    font-size: 16px;
  }
`;

const DuplicatePhoneMessage = styled.p`
  margin: var(--dcm-kiosk-duplicate-phone-message-margin, 0 0 24px);
  color: var(--dcm-kiosk-duplicate-phone-message-color, #454e50);
  font-size: var(--dcm-kiosk-duplicate-phone-message-font-size, 28px);
  text-align: var(--dcm-kiosk-duplicate-phone-message-text-align, center);

  @media ${KIOSK_PRO_MEDIA.landscapeMaxWidth}, ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    font-size: var(--dcm-kiosk-duplicate-phone-message-font-size-md, 24px);
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportPortrait}, ${KIOSK_PRO_MEDIA.smallViewportLandscapeOnly} {
    font-size: var(--dcm-kiosk-duplicate-phone-message-font-size-sm, 20px);
  }
`;

const ModalQrCodeLogin = styled(QrCodeLogin)`
  display: flex;
  align-items: center;
  flex-direction: column-reverse;
  gap: 56px;

  > p {
    font-size: 36px;
  }

  > svg {
    width: 439px;
    height: 439px;
  }
  margin-bottom: 16px;

  @media ${KIOSK_PRO_MEDIA.landscapeMaxWidth}, ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    > p {
      font-size: 28px;
    }
    > svg {
      width: 335px;
      height: 335px;
    }
    margin-bottom: 16px;
    gap: 40px;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportPortrait}, ${KIOSK_PRO_MEDIA.smallViewportLandscapeOnly} {
    > p {
      font-size: 24px;
    }
    > svg {
      width: 290px;
      height: 290px;
    }
    margin-bottom: 14px;
    gap: 36px;
  }
`;

const ModalPhoneNumberButton = styled(Button)`
  padding: 36px 56px;
  font-size: 36px;
  margin-top: 16px;
  max-width: 439px;

  @media ${KIOSK_PRO_MEDIA.landscapeMaxWidth}, ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    padding: 28px 40px;
    font-size: 28px;
    max-width: 335px;
    margin-top: 16px;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportPortrait}, ${KIOSK_PRO_MEDIA.smallViewportLandscapeOnly} {
    padding: 24px 36px;
    font-size: 24px;
    max-width: 290px;
    margin-top: 14px;
  }
`;
